/**
 * External Imports
 */
import {
  FC,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction
} from 'react';
import { connect } from 'react-redux';
import { format, add } from 'date-fns';

/**
 * Internal Imports
 */
import SideNav from '../../components/sideNav';
import SearchHeader from '../../components/searchHeader';
import PatientCensusTable from '../../components/customTables/patients/censusTable';

import { RootState } from '../../redux/store';
import {
  useGetPatientsToRoundByFacilityQuery,
  useGetRoundHistoryQuery
} from '../../redux/apis/Users.api';

import { fireSnow } from '../../services/Confetti';

import {
  isPercentLessThanOne,
  isPercentRoundedToOneHundred
} from '../../utils/complianceFunctions';
import { date as dt } from '../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, Patient, RoundHistory } from '../../config/interfaces';

/**
 * Style Imports
 */
import { CensusContainer } from './styles/PatientViews.styles';
import { cleanUnlistedPatientToRound } from '../../redux/apis/utils/cleaners';

interface CensusPageProps {
  authorizedUser?: AuthUserProps;
  searchTerm: string;
  selectedUnits: { id: string; unit: string }[];
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const PatientCensus: FC<CensusPageProps> = ({
  authorizedUser,
  searchTerm,
  selectedUnits,
  setSearchTerm
}) => {
  const [selectedTab, setSelectedTab] = useState<string>('All');
  const [rendered, setRendered] = useState(false);

  const {
    data: patientsToRoundByFacilityData = [],
    // isError: isPatientsToRoundByFacilityError,
    // isFetching: isPatientsToRoundByFacilityFetching,
    isLoading: isPatientsToRoundByFacilityLoading
  } = useGetPatientsToRoundByFacilityQuery(authorizedUser?.facilityId ?? '', {
    // TODO: Investigate effect on pagination
    // pollingInterval: 60000,
    refetchOnMountOrArgChange: true,
    skip: !authorizedUser?.facilityId
  });
  const {
    data: roundHistoryData = [],
    // isError: isRoundHistoryError,
    // isFetching: isRoundHistoryFetching,
    isLoading: isRoundHistoryLoading
  } = useGetRoundHistoryQuery(
    {
      facilityId: authorizedUser?.facilityId ?? '',
      startDate: dt.formatFilterStartDayToUTCTimestamp(
        format(new Date(), 'yyyyMMdd')
      ),
      endDate: dt.formatFilterEndDayToUTCTimestamp(
        format(add(new Date(), { days: 1 }), 'yyyyMMdd')
      ),
      roundingType: 'patient'
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !authorizedUser?.facilityId
    }
  );

  const combinedPatientsToRoundAndUnlistedData = useMemo(() => {
    const copyPatientsToRoundByFacilityData = [
      ...patientsToRoundByFacilityData
    ];
    roundHistoryData.forEach((history: RoundHistory) => {
      let duplicateFlag = false;
      if (history?.accountNum && history.accountNum.substring(0, 2) === 'UL') {
        const patient = cleanUnlistedPatientToRound(history);
        copyPatientsToRoundByFacilityData.forEach((p: Patient) => {
          if (
            p.lastRoundId === patient.lastRoundId
            // TODO: add back when backend metrics are adjusted
            // || eachPatient.accountNum === patient.accountNum
          ) {
            duplicateFlag = true;
            // TODO: See above
            // if (
            //   duplicateFlag &&
            //   compareDesc(
            //     dt.parseDate(eachPatient.lastRoundDate),
            //     dt.parseDate(patient.lastRoundDate)
            //   ) === 1
            // ) {
            //   let dup = eachPatient;
            //   dup = { ...patient };
            // }
          }
        });
        if (!duplicateFlag) {
          copyPatientsToRoundByFacilityData.unshift(patient);
        }
      }
    });
    return copyPatientsToRoundByFacilityData;
  }, [patientsToRoundByFacilityData, roundHistoryData]);

  const filteredPatientAndUnlistedRounds = useMemo(
    () =>
      combinedPatientsToRoundAndUnlistedData.filter(patient =>
        selectedUnits.some(unit => unit.id === patient.unitId)
      ),
    [combinedPatientsToRoundAndUnlistedData, selectedUnits]
  );

  const completedPatients = useMemo(
    () =>
      filteredPatientAndUnlistedRounds.filter(
        (patient: Patient) => patient.roundStatus === 'Complete'
      ),
    [filteredPatientAndUnlistedRounds]
  );
  const toDoPatients = useMemo(
    () =>
      filteredPatientAndUnlistedRounds.filter(
        (patient: Patient) => patient.roundStatus === 'To Do'
      ),
    [filteredPatientAndUnlistedRounds]
  );

  const completePercent = useMemo(
    () =>
      isPercentLessThanOne(
        completedPatients.length,
        filteredPatientAndUnlistedRounds.length
      )
        ? 1
        : isPercentRoundedToOneHundred(
            completedPatients.length,
            filteredPatientAndUnlistedRounds.length
          )
        ? 99
        : Math.round(
            (completedPatients.length /
              filteredPatientAndUnlistedRounds.length) *
              100
          ) || 0,
    [filteredPatientAndUnlistedRounds, completedPatients]
  );

  const todoPercent = useMemo(
    () =>
      filteredPatientAndUnlistedRounds.length === 0 ? 0 : 100 - completePercent,
    [filteredPatientAndUnlistedRounds, completePercent]
  );

  // *******************************************
  // ********* Removing till Phase II **********
  // *******************************************
  // const unablePatients = useMemo(
  //   () =>
  //     patientsToRound.filter((patient: Patient) => {
  //       return patient.roundStatus === 'Unable to Round';
  //     }),
  //   [patientsToRound]
  // );

  useEffect(() => {
    setRendered(true);
  }, []);

  useEffect(() => {
    if (
      window.sessionStorage.getItem('droppedPatientSnow') &&
      filteredPatientAndUnlistedRounds.length > 0 &&
      toDoPatients.length > 0
    ) {
      window.sessionStorage.removeItem('droppedPatientSnow');
    }
  }, [filteredPatientAndUnlistedRounds, toDoPatients]);

  useEffect(() => {
    if (
      rendered &&
      !window.sessionStorage.getItem('droppedPatientSnow') &&
      !(isPatientsToRoundByFacilityLoading || isRoundHistoryLoading) &&
      filteredPatientAndUnlistedRounds.length > 0 &&
      completePercent === 100
    ) {
      setTimeout(() => {
        fireSnow(
          4,
          1,
          { x: 0.16, y: 0.5 },
          [-0.4, 0.4],
          [0.4, 0.8],
          [0.4, 1],
          ['circle'],
          ['#a8a9ad']
        );
        fireSnow(
          4,
          1,
          { x: 0.16, y: 0.5 },
          [-0.4, 0.4],
          [0.4, 0.8],
          [0.4, 1],
          ['circle'],
          ['##e05a29']
        );
        fireSnow(
          4,
          1,
          { x: 0.16, y: 0.5 },
          [-0.4, 0.4],
          [0.4, 0.8],
          [0.4, 1],
          ['circle'],
          ['#206da3']
        );
      }, 1000);
      window.sessionStorage.setItem('droppedPatientSnow', 'true');
    }
  }, [filteredPatientAndUnlistedRounds, completePercent]);

  return (
    <CensusContainer id="patient-census" data-testid="patient-census">
      <SideNav
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        completeCount={completedPatients.length}
        completePercent={completePercent}
        todoPercent={todoPercent}
        todoCount={toDoPatients.length}
        totalCount={filteredPatientAndUnlistedRounds.length}
        // ********* Removing till Phase II **********
        // unableCount={unablePatients.length}
      />
      <div
        className="d-flex flex-column w-100"
        // TODO: review why webkit and overflow-x was added in divs
        // style={{ width: '-webkit-fill-available', overflowX: 'scroll' }}
      >
        <SearchHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        <PatientCensusTable
          facilityId={authorizedUser?.facilityId}
          isLoading={
            isPatientsToRoundByFacilityLoading || isRoundHistoryLoading
          }
          searchTerm={searchTerm}
          patientsToRound={
            selectedTab === 'All'
              ? [...new Set(filteredPatientAndUnlistedRounds)]
              : selectedTab === 'Completed'
              ? [...new Set(completedPatients)]
              : [...new Set(toDoPatients)]
            // ********* Removing till Phase II **********
            // : selectedTab === 'To Do' ? toDoPatients : unablePatients ? toDoPatients : unablePatients
          }
          selectedTab={selectedTab}
        />
      </div>
    </CensusContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    selectedUnits: state.ConfigReducer.selectedUnits
  };
};

export default connect(mapReduxStateToProps)(PatientCensus);
